// ============================================================================
// VOICE ACCESS (free minutes, passes, table unlock)
// ----------------------------------------------------------------------------
// Every player gets free voice/video minutes each week (45 by default),
// metered only while connected. After that: a 24h unlimited pass, or anyone
// at the table can unlock it for everyone. The server decides; this is the
// client's view of that decision plus the heartbeat that meters time.
// ============================================================================
import { getAuth } from 'firebase/auth';
import { API_BASE } from '../utils/apiBase';

export type VoiceAccessMode = 'table_unlimited' | 'pass' | 'free' | 'exhausted';

export interface VoiceAccess {
  mode: VoiceAccessMode;
  remainingSeconds: number;
  weeklySeconds: number;
  usedSeconds: number;
  resetsAtMs: number;
  passExpiresAtMs: number | null;
  tableUnlimited: boolean;
}

async function call<T>(path: string, method: 'GET' | 'POST', body?: any): Promise<T> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data as T;
}

export const getVoiceAccess = async (tableId?: string | null): Promise<VoiceAccess> =>
  (await call<{ access: VoiceAccess }>('/voice/access', 'POST', { tableId: tableId || null })).access;

export const getVoiceAllowance = async (): Promise<VoiceAccess> =>
  (await call<{ access: VoiceAccess }>('/voice/allowance', 'GET')).access;

export const voiceHeartbeat = async (tableId: string): Promise<{ access: VoiceAccess; cutoff: boolean }> =>
  call('/voice/heartbeat', 'POST', { tableId });

export const unlockTableVoice = async (
  tableId: string,
  promo?: { promoCode: string; codeId: string; agentId?: string },
): Promise<{ success: boolean; error?: string; alreadyUnlocked?: boolean; waived?: boolean }> => {
  try {
    return await call('/voice/unlockTable', 'POST', { tableId, ...(promo || {}) });
  } catch (e: any) {
    return { success: false, error: e.message || 'Could not unlock voice for this table' };
  }
};

export const formatSeconds = (s: number): string => {
  const m = Math.floor(Math.max(0, s) / 60);
  const sec = Math.max(0, s) % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

export const formatResetDay = (resetsAtMs: number): string => {
  const d = new Date(resetsAtMs);
  const now = new Date();
  const days = Math.round((d.getTime() - now.getTime()) / (24 * 3600 * 1000));
  if (days <= 0) return 'later today';
  if (days === 1) return 'tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'long' });
};

const voiceAccessService = { getVoiceAccess, getVoiceAllowance, voiceHeartbeat, unlockTableVoice, formatSeconds, formatResetDay };
export default voiceAccessService;
